/**
 * Sarup desktop nav — hover intent, leave grace, outside-click.
 *
 * PROGRESSIVE ENHANCEMENT, deliberately. The markup is <details>/<summary>,
 * so before this file runs the menu already opens on click and on Enter or
 * Space, is focusable, and closes on Escape — all of it the browser's own
 * behaviour. This adds only the three things <details> cannot do by itself:
 *
 *   · open on hover after a 120ms intent delay, so crossing the bar on the
 *     way somewhere else does not flash panels open
 *   · close 220ms after the pointer leaves, so a diagonal move from the
 *     trigger down into the panel does not lose it
 *   · close on a click outside the nav
 *
 * Both timings are the contract's. Escape is handled here too, because a
 * native <details> only closes on Escape while focus is inside it, and the
 * contract wants Escape to close the panel from anywhere.
 *
 * The open state is the `open` attribute on <details>. Nothing here keeps
 * state of its own, so the CSS has exactly one thing to style and there is no
 * way for script state and DOM state to drift apart.
 *
 * Touch: a tap fires the summary's native click, which toggles the details.
 * Hover listeners never fire on a touch device, so no special-casing.
 */
(() => {
  const OPEN_DELAY = 120;
  const CLOSE_GRACE = 220;

  class SarupNavItem extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;

      this.summary = this.details.querySelector('summary');
      this.openTimer = null;
      this.closeTimer = null;

      // Hover intent. Only on devices that actually hover — a coarse pointer
      // would otherwise open the panel on the tap that also toggles it.
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.addEventListener('mouseenter', this.onEnter);
        this.addEventListener('mouseleave', this.onLeave);
      }

      // Keyboard focus moving into this item cancels a pending close, so
      // tabbing from the trigger into the panel cannot close it underneath.
      this.addEventListener('focusin', () => clearTimeout(this.closeTimer));
    }

    disconnectedCallback() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    onEnter = () => {
      clearTimeout(this.closeTimer);
      this.openTimer = setTimeout(() => {
        // Never let a stray pointer crossing the bar throw a panel over a
        // search someone is typing into. A deliberate click still opens it,
        // because that path does not come through here.
        if (SarupNavItem.searchIsOpen()) return;
        SarupNavItem.closeAll(this);
        this.details.open = true;
      }, OPEN_DELAY);
    };

    static searchIsOpen() {
      return [...document.querySelectorAll('predictive-search')].some((ps) =>
        ps.hasAttribute('open')
      );
    }

    onLeave = () => {
      clearTimeout(this.openTimer);
      this.closeTimer = setTimeout(() => {
        this.details.open = false;
      }, CLOSE_GRACE);
    };

    static closeAll(except) {
      document.querySelectorAll('sarup-nav-item').forEach((el) => {
        if (el === except) return;
        const d = el.querySelector('details');
        if (d) d.open = false;
      });
    }
  }

  if (!customElements.get('sarup-nav-item')) {
    customElements.define('sarup-nav-item', SarupNavItem);
  }

  // Only one panel at a time, including when opened by click or keyboard.
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    if (!d.closest('sarup-nav-item')) return;
    SarupNavItem.closeAll(d.closest('sarup-nav-item'));
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') SarupNavItem.closeAll(null);
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('sarup-nav-item')) SarupNavItem.closeAll(null);
  });

  /* ------------------------------------------------------------------
     ONE OVERLAY AT A TIME — the panel and predictive search both drop out
     of the header, and they were overlapping each other.

     Both directions are driven off REAL state, not a guess about which
     event means "opening". Dawn's <predictive-search> sets an `open`
     attribute on itself in open() and removes it in close(), so an
     attribute observer is exactly as accurate as Dawn's own notion of
     being open — it cannot drift, and it catches every route in
     (typing, focus with a previous term, arrow keys) without this file
     having to know about any of them.

     close() is called WITHOUT clearing the search term: dismissing the
     results because a menu opened must not throw away what was typed.
     ------------------------------------------------------------------ */
  const searches = () => document.querySelectorAll('predictive-search');

  const closeSearch = () => {
    searches().forEach((ps) => {
      if (ps.hasAttribute('open') && typeof ps.close === 'function') ps.close();
    });
  };

  const anySearchOpen = () => [...searches()].some((ps) => ps.hasAttribute('open'));

  // Panel opening closes search. Hooks the same toggle event used above, so
  // click, keyboard and hover-intent all route through it.
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open) return;
    if (d.closest('sarup-nav-item')) closeSearch();
  }, true);

  // Search opening closes the panel.
  if ('MutationObserver' in window) {
    const watch = new MutationObserver((records) => {
      for (const r of records) {
        if (r.attributeName === 'open' && r.target.hasAttribute('open')) {
          SarupNavItem.closeAll(null);
          return;
        }
      }
    });
    const attach = () => searches().forEach((ps) =>
      watch.observe(ps, { attributes: true, attributeFilter: ['open'] })
    );
    attach();
    // The header re-renders on section reload in the theme editor, which
    // replaces these elements; re-attach so the rule survives an edit.
    document.addEventListener('shopify:section:load', attach);
  }

  document.addEventListener('focusin', (e) => {
    if (e.target.closest('predictive-search') && anySearchOpen()) {
      SarupNavItem.closeAll(null);
    }
  });
})();
